import React from "react";
import { useRoute } from "@react-navigation/native";
import { RootRouteProps } from "routers/types/stack";

import {
  View,
  Linking,
  ScrollView,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
} from "react-native";
import { Error, Header, ImgIcon, Loading, Text } from "components";

import { colors, sizes, strings } from "assets/constants";
import { useCompany } from "clients/apis";
import { copyToClipboard, mq } from "utils";
import css from "styles";
import { icons } from "assets/icons";
import { _callTo, _mailTo, _openLink } from "routers/helper";

export default function Company() {
  const { id } = useRoute<RootRouteProps<"company">>().params;

  const { data, isLoading, isError, error, refetch, isRefetching } =
    useCompany(id);

  // const {name, brand_name, address, description, phone, socials} = data?.data;
  const company = data?.data;
  const site = company?.socials.filter((s: any) => s.type === "site")[0];
  const email = company?.socials.filter((s: any) => s.type === "email")[0];
  const Q = company?.socials.filter(
    (s: any) => s.type !== "site" && s.type !== "email"
  );

  const _onGoToSocial = (url: string) => Linking.openURL(url);

  const _renderContent = () => {
    if (isLoading) {
      return <Loading style={{ marginTop: mq(20) }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <ScrollView
        contentContainerStyle={{
          ...css.paddingH,
          paddingVertical: sizes.space,
        }}
      >
        <View
          style={{
            ...css.rRowCenterAlign,
            padding: mq(5),
            backgroundColor: colors.white,
            borderWidth: mq(5),
            borderRadius: mq(15),
            borderColor: colors.lightButtons,
            elevation: mq(20),
          }}
        >
          <View
            style={{
              width: mq(70),
              height: mq(70),
              marginLeft: mq(10),
              ...css.centralElements,
            }}
          >
            <ImgIcon
              style={{ marginTop: mq(15) }}
              icon={icons.office}
              size={mq(48)}
            />
            <View
              style={{
                ...css.absolute,
                ...css.fitWidth,
                ...css.fitHeight,
                borderWidth: mq(7),
                borderRadius: mq(100),
                borderColor: colors.lightButtons,
              }}
            />
          </View>
          <View style={css.container}>
            {company?.name && (
              <Text weight="extraBold" size={"lg"} numberOfLines={2}>
                {company?.name}
              </Text>
            )}
            {company?.brand_name && (
              <View style={{ ...css.rRowCenterAlign }}>
                <ImgIcon
                  icon={icons.brand}
                  size={mq(20)}
                  color={colors.iconColor}
                  style={{ top: mq(1) }}
                />
                <Text size={"xs"} style={css.container} numberOfLines={2}>
                  {company?.brand_name}
                </Text>
              </View>
            )}
          </View>
        </View>

        {company?.address && (
          <View style={{ ...css.rRow, marginTop: mq(30) }}>
            <View style={{ width: mq(80) }}>
              <Text weight="bold" color={colors.textColorGray}>
                {strings.address}
              </Text>
            </View>
            <View style={css.container}>
              <TouchableOpacity
                style={{ ...css.selfEnd }}
                activeOpacity={sizes.tapOpacity}
                onPress={() => copyToClipboard(company?.address)}
              >
                <Text weight="bold" style={{ lineHeight: mq(22) }}>
                  {company?.address}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {company?.phone && (
          <View style={{ ...css.rRowCenterAlign, marginTop: mq(30) }}>
            <View style={{ width: mq(80) }}>
              <Text weight="bold" color={colors.textColorGray}>
                {strings.telephone}
              </Text>
            </View>
            <View style={css.container}>
              <TouchableOpacity
                style={{ ...css.selfEnd }}
                activeOpacity={sizes.tapOpacity}
                onPress={() => _callTo(company?.phone)}
              >
                <Text weight="bold" style={{ lineHeight: mq(22) }}>
                  {company?.phone}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {site?.value && (
          <View style={{ ...css.rRowCenterAlign, marginTop: mq(30) }}>
            <View style={{ width: mq(80) }}>
              <Text weight="bold" color={colors.textColorGray}>
                {strings.website}
              </Text>
            </View>
            <View style={css.container}>
              <TouchableOpacity
                style={{ ...css.selfEnd }}
                activeOpacity={sizes.tapOpacity}
                onPress={() => _openLink(site?.value)}
              >
                <Text weight="bold" style={{ lineHeight: mq(22) }}>
                  {site?.value}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {email?.value && (
          <View style={{ ...css.rRow, marginTop: mq(30) }}>
            <View style={{ width: mq(80) }}>
              <Text weight="bold" color={colors.textColorGray}>
                {strings.email}
              </Text>
            </View>
            <View style={css.container}>
              <TouchableOpacity
                style={{ ...css.selfEnd }}
                activeOpacity={sizes.tapOpacity}
                onPress={() => _mailTo(email?.value)}
              >
                <Text weight="bold" style={{ lineHeight: mq(22) }}>
                  {email?.value}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View
          style={{
            ...css.fitWidth,
            height: mq(5),
            borderRadius: mq(5),
            backgroundColor: colors.lightButtons,
            marginTop: mq(50),
          }}
        />

        <View
          style={{
            marginTop: mq(20),
            ...css.selfCenter,
            ...css.rRowCenterAlign,
          }}
        >
          {Q?.map((social: any, key: number) => {
            if (social?.value) {
              return (
                <TouchableOpacity
                  activeOpacity={sizes.tapOpacity}
                  onPress={() => social.value && _onGoToSocial(social.value)}
                  key={String(key)}
                  style={styles.socialBox}
                >
                  <ImgIcon icon={{ uri: social?.image }} size={"90%"} />
                </TouchableOpacity>
              );
            }
          })}
        </View>
      </ScrollView>
    );
  };

  return (
    <>
      <Header title={strings.company_infos} />
      <SafeAreaView style={{ ...css.container }}>
        {_renderContent()}
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  socialBox: {
    marginHorizontal: mq(15),
    width: 60,
    aspectRatio: 1,
  },
});
